import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import "./App.css";
import PrivacyPolicyPage from "./pages/PrivacyPolicy";
import SupportPage from "./pages/Support";
import { LanguageProvider, useLanguage } from "./LanguageContext";

function LandingPage() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t, language, toggleLanguage } = useLanguage();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="app">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <Link to="/" className="nav-logo-link">
              <img
                src="/belly-flow-logo.svg"
                alt="Belly Flow"
                className="nav-logo-img"
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="nav-links desktop-nav">
            <a href="#features" className="nav-link">
              {t('features')}
            </a>
            <a href="#about" className="nav-link">
              {t('about')}
            </a>
            <Link to="/privacy" className="nav-link">
              {t('privacy')}
            </Link>
            <Link to="/support" className="nav-link">
              {t('support')}
            </Link>
            
            {/* Language Switcher */}
            <div className="language-switcher">
              <button 
                className="language-button"
                onClick={toggleLanguage}
                aria-label={t('language')}
              >
                {language === 'sv' ? 'EN' : 'SV'}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Right Side */}
          <div className="mobile-nav-right">
            {/* Mobile Language Switcher - to the left of hamburger menu */}
            <div className="mobile-language-switcher-top">
              <button 
                className="language-button mobile-top"
                onClick={toggleLanguage}
              >
                {language === 'sv' ? 'EN' : 'SV'}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="mobile-menu-button"
              onClick={toggleMobileMenu}
              aria-label="Öppna meny"
            >
              <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
              <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
              <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-menu-links">
            <a href="#features" className="mobile-nav-link" onClick={closeMobileMenu}>
              {t('features')}
            </a>
            <a href="#about" className="mobile-nav-link" onClick={closeMobileMenu}>
              {t('about')}
            </a>
            <Link to="/privacy" className="mobile-nav-link" onClick={closeMobileMenu}>
              {t('privacy')}
            </Link>
            <Link to="/support" className="mobile-nav-link" onClick={closeMobileMenu}>
              {t('support')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              {t('heroTitle')}
            </h1>
            <p className="hero-subtitle">
              {t('heroSubtitle')}
            </p>
            <div className="hero-cta">
              <div className="store-badges">
                <a 
                  href="https://apps.apple.com/se/app/belly-flow/id6751012592" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="store-badge"
                >
                  <img src="/app-store-badge.svg" alt="Download on the App Store" />
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.bellyflow.app" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="store-badge"
                >
                  <img src="/google-play-badge.svg" alt="Get it on Google Play" />
                </a>
              </div>
            </div>
          </div>
          <div className="hero-image">
            <img
              src="/belly-flow-logo.svg"
              alt="BellyFlow App"
              className="hero-logo"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <div className="features-grid">
            <div className="feature-card">
              <h3 className="feature-title">{t('simpleLogging')}</h3>
              <p className="feature-description">
                {t('simpleLoggingDesc')}
              </p>
            </div>
            <div className="feature-card">
              <h3 className="feature-title">{t('trendsAnalysis')}</h3>
              <p className="feature-description">
                {t('trendsAnalysisDesc')}
              </p>
            </div>
            <div className="feature-card">
              <h3 className="feature-title">{t('research')}</h3>
              <p className="feature-description">
                {t('researchDesc')}
              </p>
            </div>
            <div className="feature-card">
              <h3 className="feature-title">{t('securePrivate')}</h3>
              <p className="feature-description">
                {t('securePrivateDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <h2 className="section-title">{t('aboutTitle')}</h2>
            <p className="about-description">
              {t('aboutDesc1')}
            </p>
            <p className="about-description">
              {t('aboutDesc2')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2 className="cta-title">{t('ctaTitle')}</h2>
          <p className="cta-subtitle">
            {t('ctaSubtitle')}
          </p>
          <div className="store-badges center">
            <a 
              href="https://apps.apple.com/se/app/belly-flow/id6751012592"
              target="_blank"
              rel="noopener noreferrer"
              className="store-badge"
            >
              <img src="/app-store-badge.svg" alt="Download on the App Store" />
            </a>
            <a 
              href="https://play.google.com/store/apps/details?id=com.bellyflow.app"
              target="_blank"
              rel="noopener noreferrer"
              className="store-badge"
            >
              <img src="/google-play-badge.svg" alt="Get it on Google Play" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <img
                  src="/belly-flow-logo.svg"
                  alt="Belly Flow"
                  className="footer-logo-img"
                />
              </div>
              <p className="footer-description">
                {t('footerDescription')}
              </p>
            </div>
            <div className="footer-section">
              <h4 className="footer-title">{t('links')}</h4>
              <a href="#features" className="footer-link">
                {t('features')}
              </a>
              <a href="#about" className="footer-link">
                {t('about')}
              </a>
              <Link to="/privacy" className="footer-link">
                {t('privacy')}
              </Link>
              <Link to="/support" className="footer-link">
                {t('support')}
              </Link>
            </div>
            <div className="footer-section">
              <h4 className="footer-title">{t('contact')}</h4>
              <a href="mailto:support@bellyflow.se" className="footer-link">
                support@bellyflow.se
              </a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>{t('copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <Router>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/support" element={<SupportPage />} />
        </Routes>
      </Router>
    </LanguageProvider>
  );
}

export default App;
